import { useState, useEffect } from 'react';
import { useTheme } from '../theme';
import { db } from '../dbClient';
import { useToast } from '../Toast';

const ENTITY_TYPES = [
  { value: 'customer', label: '👤 Customers' },
  { value: 'third_party', label: '🏢 Third Parties' },
  { value: 'party', label: '👥 Parties' },
  { value: 'counterparty', label: '💼 Counterparties' },
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const AVAILABLE_SOURCES = [
  { value: 'ofac', label: 'OFAC Sanctions List' },
  { value: 'serpro', label: 'Serpro (Brazil)' },
  { value: 'un_sanctions', label: 'UN Sanctions' },
  { value: 'eu_sanctions', label: 'EU Consolidated List' },
  { value: 'adverse_media', label: 'Adverse Media Search' },
  { value: 'blockscout', label: 'BlockScout Blockchain' },
  { value: 'pep_scan', label: 'PEP/Family Scan' },
];

export default function MonitoringConfiguration({ company }) {
  const C = useTheme();
  const { showToast } = useToast();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [stats, setStats] = useState(null);
  const [formData, setFormData] = useState({
    entity_type: 'customer',
    frequency: 'weekly',
    day_of_week: 2, // Tuesday
    day_of_month: 15,
    hour_utc: 2,
    sources_to_scan: ['ofac', 'adverse_media'],
    is_active: true,
  });

  useEffect(() => {
    if (company?.id) {
      loadSchedules();
      loadStats();
    }
  }, [company?.id]);

  async function loadSchedules() {
    if (!company?.id) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/monitoring/schedules?companyId=${company.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('amlguard_token')}` }
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSchedules(data || []);
    } catch (err) {
      showToast('Error loading monitoring schedules', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    if (!company?.id) return;
    try {
      const { data, error } = await db
        .from('monitoring_runs')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      const totalRuns = data?.length || 0;
      const successfulRuns = data?.filter(r => r.status === 'completed').length || 0;
      setStats({ total_runs: totalRuns, successful_runs: successfulRuns, success_rate: totalRuns ? Math.round((successfulRuns / totalRuns) * 100) : 0 });
    } catch (err) {
      console.error('Error loading monitoring stats:', err);
    }
  }

  async function handleSave() {
    if (!company?.id) {
      showToast('Company not found', 'error');
      return;
    }

    try {
      const payload = {
        companyId: company.id,
        entity_type: formData.entity_type,
        frequency: formData.frequency,
        day_of_week: formData.day_of_week,
        day_of_month: formData.day_of_month,
        hour_utc: formData.hour_utc,
        sources_to_scan: Array.isArray(formData.sources_to_scan) ? formData.sources_to_scan : [],
        is_active: formData.is_active,
      };

      if (editingId) {
        payload.id = editingId;
      }

      const res = await fetch('/api/monitoring/schedules', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('amlguard_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }

      showToast(editingId ? 'Schedule updated' : 'Schedule created', 'success');
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadSchedules();
      loadStats();
    } catch (err) {
      showToast('Error saving schedule', 'error');
      console.error(err);
    }
  }

  function handleEdit(schedule) {
    setEditingId(schedule.id);
    setFormData({
      entity_type: schedule.entity_type,
      frequency: schedule.frequency,
      day_of_week: schedule.day_of_week || 2,
      day_of_month: schedule.day_of_month || 15,
      hour_utc: schedule.hour_utc || 2,
      sources_to_scan: schedule.sources_to_scan || [],
      is_active: schedule.is_active,
    });
    setShowForm(true);
  }

  async function handleDelete(id) {
    if (!confirm('Delete this monitoring schedule?')) return;
    try {
      const res = await fetch('/api/monitoring/schedules', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('amlguard_token')}`
        },
        body: JSON.stringify({ id, companyId: company.id })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }

      showToast('Schedule deleted', 'success');
      loadSchedules();
    } catch (err) {
      showToast('Error deleting schedule', 'error');
      console.error(err);
    }
  }

  function resetForm() {
    setFormData({
      entity_type: 'customer',
      frequency: 'weekly',
      day_of_week: 2,
      day_of_month: 15,
      hour_utc: 2,
      sources_to_scan: ['ofac', 'adverse_media'],
      is_active: true,
    });
  }

  const getEntityLabel = (type) => ENTITY_TYPES.find(e => e.value === type)?.label || type;

  if (loading) {
    return <div style={{ padding: '2rem', color: C.textDim }}>Loading monitoring configuration...</div>;
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: C.text, marginBottom: '0.5rem', fontFamily: "'Syne', sans-serif" }}>
          Automated Monitoring Schedules
        </h2>
        <p style={{ color: C.textDim, fontSize: '0.9rem' }}>
          Configure automatic re-screening of active customers, third parties, and counterparties.
        </p>
      </div>

      {/* Statistics */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {[
            { label: 'Monitored Entities', value: stats.total_monitored || 0 },
            { label: 'New Hits (30d)', value: stats.new_hits || 0, color: '#FF6B6B' },
            { label: 'Cleared (30d)', value: stats.cleared_count || 0, color: '#4CAF50' },
            { label: 'Alerts Generated', value: stats.alerts_generated || 0, color: '#FF9800' },
          ].map(s => (
            <div
              key={s.label}
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: '1rem',
                textAlign: 'center'
              }}
            >
              <div style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                {s.label}
              </div>
              <div style={{ color: s.color || C.accent, fontWeight: 700, fontSize: '1.8rem' }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif" }}>
            {editingId ? 'Edit Monitoring Schedule' : 'Create New Schedule'}
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Entity Type *
              </label>
              <select
                value={formData.entity_type}
                onChange={(e) => setFormData({...formData, entity_type: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              >
                {ENTITY_TYPES.map(e => (
                  <option key={e.value} value={e.value}>{e.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Frequency *
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              >
                {FREQUENCIES.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          {formData.frequency === 'weekly' && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Day of Week
              </label>
              <select
                value={formData.day_of_week}
                onChange={(e) => setFormData({...formData, day_of_week: parseInt(e.target.value)})}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              >
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, idx) => (
                  <option key={idx} value={idx}>{day}</option>
                ))}
              </select>
            </div>
          )}

          {formData.frequency === 'monthly' && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Day of Month (1-28)
              </label>
              <input
                type="number"
                value={formData.day_of_month}
                onChange={(e) => setFormData({...formData, day_of_month: Math.min(28, parseInt(e.target.value) || 1)})}
                min={1}
                max={28}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
              Run Time (UTC)
            </label>
            <input
              type="number"
              value={formData.hour_utc}
              onChange={(e) => setFormData({...formData, hour_utc: Math.max(0, Math.min(23, parseInt(e.target.value) || 0))})}
              min={0}
              max={23}
              style={{
                width: '100%',
                padding: '0.6rem',
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.text,
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.9rem'
              }}
            />
            <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.25rem' }}>
              Hour: {String(formData.hour_utc).padStart(2, '0')}:00 UTC
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
              Data Sources to Scan
            </label>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {AVAILABLE_SOURCES.map(source => (
                <label key={source.value} style={{ color: C.text, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.sources_to_scan.includes(source.value)}
                    onChange={(e) => {
                      const updated = e.target.checked
                        ? [...formData.sources_to_scan, source.value]
                        : formData.sources_to_scan.filter(s => s !== source.value);
                      setFormData({...formData, sources_to_scan: updated});
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.9rem' }}>{source.label}</span>
                </label>
              ))}
            </div>
          </div>

          <label style={{ color: C.text, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '1rem' }}>
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.9rem' }}>Active</span>
          </label>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleSave}
              style={{
                padding: '0.6rem 1.2rem',
                background: C.accent,
                color: '#0A0E1A',
                border: 'none',
                borderRadius: 6,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.85rem'
              }}
            >
              Save Schedule
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                resetForm();
              }}
              style={{
                padding: '0.6rem 1.2rem',
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.textMid,
                cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.85rem'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!showForm && (
        <button
          onClick={() => {
            setShowForm(true);
            resetForm();
          }}
          style={{
            padding: '0.6rem 1.2rem',
            background: `${C.accent}22`,
            border: `1px solid ${C.accent}`,
            color: C.accent,
            borderRadius: 6,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.85rem',
            marginBottom: '2rem'
          }}
        >
          + New Monitoring Schedule
        </button>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {schedules.length === 0 ? (
          <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: '2rem',
            textAlign: 'center',
            color: C.textDim
          }}>
            No monitoring schedules configured. Click "New Monitoring Schedule" to get started.
          </div>
        ) : (
          schedules.map(schedule => (
            <div
              key={schedule.id}
              style={{
                background: schedule.is_active ? C.surface : `${C.surface}80`,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: '1rem',
                opacity: schedule.is_active ? 1 : 0.6
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                <div>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>
                    {getEntityLabel(schedule.entity_type)}
                    {!schedule.is_active && (
                      <span style={{ color: C.textDim, fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                        (Inactive)
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleEdit(schedule)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'transparent',
                      border: `1px solid ${C.border}`,
                      color: C.text,
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: "'DM Mono', monospace"
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(schedule.id)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'transparent',
                      border: `1px solid #FF6B6B66`,
                      color: '#FF6B6B',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: "'DM Mono', monospace"
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.85rem' }}>
                <div>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
                    FREQUENCY
                  </div>
                  <div style={{ color: C.text }}>{schedule.frequency}</div>
                </div>
                <div>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
                    RUN TIME
                  </div>
                  <div style={{ color: C.text }}>{String(schedule.hour_utc || 2).padStart(2, '0')}:00 UTC</div>
                </div>
                <div>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
                    LAST RUN
                  </div>
                  <div style={{ color: C.text }}>
                    {schedule.last_run_at ? new Date(schedule.last_run_at).toLocaleDateString() : 'Never'}
                  </div>
                </div>
                <div>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
                    NEXT RUN
                  </div>
                  <div style={{ color: C.accent }}>
                    {schedule.next_run_at ? new Date(schedule.next_run_at).toLocaleDateString() : '—'}
                  </div>
                </div>
              </div>

              {schedule.sources_to_scan && schedule.sources_to_scan.length > 0 && (
                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: `1px solid ${C.border}` }}>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                    Sources
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {schedule.sources_to_scan.map(source => (
                      <span
                        key={source}
                        style={{
                          background: `${C.accent}22`,
                          color: C.accent,
                          padding: '0.25rem 0.6rem',
                          borderRadius: 3,
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      >
                        {AVAILABLE_SOURCES.find(s => s.value === source)?.label || source}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
